// src/pages/member/components/CoursePointsTables.jsx
export default function ChineseCoursePointsTables({ coursePoints = [] }) {
  if (!Array.isArray(coursePoints) || coursePoints.length === 0) {
    return <p>載入中...</p>;
  }

  // ==== 1) id -> 顯示名稱（你提供的對照表）====
  const DISPLAY_BY_ID = {
    1: "FAR 財務會計與報告",
    2: "AUD 審計與鑑證",
    3: "REG 稅務與法規",
    4: "BAR 商業分析與報告",
    5: "ISC 資訊系統與控制",
    6: "TCP 稅務遵循與規劃",
    7: "Part 1：個人稅",
    8: "Part 2：企業稅",
    9: "Part 3：代理、實務與程序",
    10: "Part 1：《財務規劃、績效與分析》",
    11: "Part 2：《策略財務管理》",
  };

  // ==== 2) 名稱 -> id（常見寫法/別名都盡量吃）====
  const NAME_TO_ID = (() => {
    const m = new Map();
    // CPA 六科
    m.set("FAR", 1);
    m.set("AUD", 2);
    m.set("REG", 3);
    m.set("BAR", 4);
    m.set("ISC", 5);
    m.set("TCP", 6);

    // EA 三科（接受多種寫法）
    const ea1 = ["EA PART 1", "EA-PART1", "EA_PART1", "EA1", "EA P1", "EA-1"];
    const ea2 = ["EA PART 2", "EA-PART2", "EA_PART2", "EA2", "EA P2", "EA-2"];
    const ea3 = ["EA PART 3", "EA-PART3", "EA_PART3", "EA3", "EA P3", "EA-3"];
    ea1.forEach((k) => m.set(k, 7));
    ea2.forEach((k) => m.set(k, 8));
    ea3.forEach((k) => m.set(k, 9));

    // CMA 兩科（接受多種寫法）
    const cma1 = [
      "CMA PART 1",
      "CMA-PART1",
      "CMA_PART1",
      "CMA1",
      "CMA P1",
      "CMA-1",
      "CMA PART 1：《財務規劃、績效與分析》",
    ];
    const cma2 = [
      "CMA PART 2",
      "CMA-PART2",
      "CMA_PART2",
      "CMA2",
      "CMA P2",
      "CMA-2",
      "CMA PART 2：《策略財務管理》",
    ];
    cma1.forEach((k) => m.set(k, 10));
    cma2.forEach((k) => m.set(k, 11));

    return m;
  })();

  // 輔助：把各種名稱正規化（移除多餘空白、轉大寫）
  const norm = (s = "") => String(s).trim().replace(/\s+/g, " ").toUpperCase();

  // 若後端只回 "EA" / "CMA"（沒有分 Part），就無法唯一對應到 7~9 / 10~11
  // 這裡先嘗試以關鍵字推斷；若仍只有 "EA"、"CMA" 這種總稱，就回傳 null（會歸到未分類，不顯示）
  const resolveId = (name) => {
    const n = norm(name);

    // 直接命中
    if (NAME_TO_ID.has(n)) return NAME_TO_ID.get(n);

    // 常見 CPA 代碼
    if (["FAR", "AUD", "REG", "BAR", "ISC", "TCP"].includes(n)) {
      return NAME_TO_ID.get(n);
    }

    // EA 推斷
    if (/^EA[\s\-]?PART[\s\-]?1$/i.test(name)) return 7;
    if (/^EA[\s\-]?PART[\s\-]?2$/i.test(name)) return 8;
    if (/^EA[\s\-]?PART[\s\-]?3$/i.test(name)) return 9;

    // CMA 推斷
    if (/^CMA[\s\-]?PART[\s\-]?1/i.test(name)) return 10;
    if (/^CMA[\s\-]?PART[\s\-]?2/i.test(name)) return 11;

    // 純 "EA" 或 "CMA" 無法唯一對應，回 null（避免誤分）
    if (n === "EA" || n === "CMA") return null;

    return null;
  };

  // ==== 3) 整理成 {id, displayName, points} 後分群 ====
  const normalized = coursePoints
    .map((r, idx) => {
      const id = resolveId(r.course_name);
      return {
        key: idx,
        id,
        displayName: id ? DISPLAY_BY_ID[id] : r.course_name, // 無法對應時就顯示原名
        points: Number(r.points || 0),
      };
    })
    .filter((r) => r.id != null); // 無 id（未能對應）的先不顯示

  const CPA = normalized.filter((r) => r.id >= 1 && r.id <= 6).sort((a, b) => a.id - b.id);
  const EA = normalized.filter((r) => r.id >= 7 && r.id <= 9).sort((a, b) => a.id - b.id);
  const CMA = normalized.filter((r) => r.id >= 10 && r.id <= 11).sort((a, b) => a.id - b.id);

  const Table = ({ title, data }) => (
    <div className="mb-4">
      <h4 className="mb-2">{title}</h4>
      <div className="table-responsive">
        <table className="table table-bordered table-striped table-condensed">
          <thead>
            <tr>
              <th>科目名稱</th>
              <th>中文課程堂數</th>
              <th>所需觀看點數</th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((c) => (
                <tr key={`${title}-${c.key}`}>
                  <td>{c.displayName}</td>
                  <td>{c.points}</td>
                  <td>{c.points}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="text-center text-muted">（尚無資料）</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <>
      <Table title="CPA" data={CPA} />
      <Table title="EA"  data={EA} />
      <Table title="CMA" data={CMA} />
    </>
  );
}
